import {
  Avatar,
  Button,
  Flex,
  FlexProps,
  GridItem,
  Icon,
  Input,
  SimpleGrid,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
} from '@chakra-ui/react';
import { getTenantId } from '@pelicargo/tenants';
import { TSEquals, TSList } from '@pelicargo/utils';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { HiUserPlus } from 'react-icons/hi2';

import { useTypesense } from '../../hooks/useTypesense';
import { CONTACT_TAGS } from '../../utils/constants';
import { ContactCard } from '../ContactCard';
import { Label } from '../LabeledText';
import { ReactSelect } from '../ReactSelect';
import { useRequestDrawersContext } from '../RequestDrawers';
import { TypesenseSelect } from '../TypesenseSelect';
import { ContactHit, useSimpleContactSearch } from './Context';

type RemoveType = 'account' | 'provider' | 'airport' | 'tag';

type SearchState = {
  query?: string;
  accounts?: { name: string; id: number }[];
  providerIds?: string[];
  airportIds?: string[];
  tags?: string[];
};

type ResultState = {
  data: ContactHit[] | [];
  totalCount?: number;
  page?: number;
};

const ITEMS_PER_PAGE = 9;
export const Form = (rest: FlexProps) => {
  const { typesenseClient } = useTypesense({}, true);

  const {
    checkIsSelected,
    onSelectContact,
    onRemoveContact,
    defaultAccounts,
    defaultProviderIds,
    defaultAirportIds,
    defaultTags,
    defaultQuery,
    searchPattern,
  } = useSimpleContactSearch();

  const { openModal, closeModal } = useRequestDrawersContext();

  const [results, setResults] = useState<ResultState>({
    data: [],
    page: 1,
    totalCount: 0,
  });

  const numberOfPages = useMemo(() => {
    return Math.ceil(results?.totalCount / ITEMS_PER_PAGE);
  }, [results?.totalCount]);

  const [search, setSearch] = useState<SearchState>({
    query: defaultQuery,
    accounts: defaultAccounts ?? [],
    providerIds: defaultProviderIds ?? [],
    airportIds: defaultAirportIds ?? [],
    tags: defaultTags ?? [],
  });

  const clearSearch = () => {
    setSearch({
      query: '',
      accounts: [],
      providerIds: [],
      airportIds: [],
      tags: [],
    });
  };

  const getFilterForSearchPattern = useCallback(() => {
    const accountsIds = search.accounts?.map((account) => account?.id);
    const hasAccounts = search.accounts.length !== 0 ? false : undefined;
    const hasAirports = search.airportIds.length !== 0 ? false : undefined;
    const tenantCheck = TSEquals('tenant_ids', [getTenantId()]);

    // Airline Search Pattern
    if (searchPattern === 'AIRLINE') {
      return TSList('and', [
        tenantCheck,

        // required provider
        TSEquals('associations.providers', search.providerIds),

        // required tag
        TSEquals('associations.tags', search.tags),

        // optional airport
        TSList('or', [
          TSEquals('associations.airports', search.airportIds),
          TSEquals('associations.has_airports', hasAirports),
        ]),

        // optional account
        TSList('or', [
          TSEquals('associations.accounts.id', accountsIds),
          TSEquals('associations.has_accounts', hasAccounts),
        ]),
      ]);
    }

    // FF Search Pattern
    return TSList('and', [
      tenantCheck,
      // required account
      TSEquals('associations.accounts.id', accountsIds),

      // required tag
      TSEquals('associations.tags', search.tags),

      // ensure no providers
      TSList('or', [TSEquals('associations.providers', []), TSEquals('associations.has_airlines', false)]),

      // optional airport
      TSList('or', [
        TSEquals('associations.airports', search.airportIds),
        TSEquals('associations.has_airports', hasAirports),
      ]),
    ]);
  }, [search.providerIds, search.airportIds, search.accounts, search.tags, searchPattern]);

  const handleTypesenseSearch = useCallback(async () => {
    const res = await typesenseClient
      ?.collections('contacts')
      .documents()
      .search({
        q: search.query ?? '',
        query_by: 'full_name, preferred_name, email',
        facet_by: 'associations.providers, associations.airports, associations.accounts.id, associations.tags',
        filter_by: getFilterForSearchPattern(),
        per_page: ITEMS_PER_PAGE,
        page: results.page,
        sort_by: 'full_name:asc',
      });

    setResults({
      data: res?.hits?.map((result) => result.document as ContactHit) ?? [],
      totalCount: res?.found,
      page: results.page,
    });
  }, [search, setResults, results.page, getFilterForSearchPattern, typesenseClient]);

  useEffect(() => {
    handleTypesenseSearch();
  }, [search, results?.page, handleTypesenseSearch]);

  const handleQueryChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearch((prev) => ({ ...prev, query: event.target.value }));
    resetPage();
  }, []);

  const resetPage = () => setResults((prev) => ({ ...prev, page: 1 }));
  const incrementPage = () => setResults((prev) => ({ ...prev, page: prev.page + 1 }));
  const decrementPage = () => setResults((prev) => ({ ...prev, page: prev.page - 1 }));

  const handleContactCreated = (contact: ContactHit) => {
    onSelectContact?.(contact);
    handleTypesenseSearch();

    closeModal('Contact');
  };

  const renderContactCard = (contact: ContactHit) => {
    const isSelected = checkIsSelected?.(contact);

    return (
      <ContactCard
        key={contact?.id}
        contact={contact}
        isSelected={isSelected}
        onSelect={onSelectContact}
        onRemove={onRemoveContact}
        onUpdate={handleTypesenseSearch}
      />
    );
  };

  const handleRemove = (id: string | number, type: RemoveType) => () => {
    if (type === 'account') {
      setSearch((prev) => ({ ...prev, accounts: prev.accounts.filter((node) => node?.id !== id) }));
    }
    if (type === 'provider') {
      setSearch((prev) => ({ ...prev, providerIds: prev.providerIds.filter((node) => node !== id) }));
    }
    if (type === 'airport') {
      setSearch((prev) => ({ ...prev, airportIds: prev.airportIds.filter((node) => node !== id) }));
    }
    if (type === 'tag') setSearch((prev) => ({ ...prev, tags: prev.tags.filter((node) => node !== id) }));

    resetPage();
  };

  const handleFilterChange = useCallback(
    (filterType: string) => (nextValue: any) => {
      if (filterType === 'account') {
        setSearch((prev) => ({ ...prev, accounts: [{ name: nextValue?.name_label, id: nextValue?.value }] }));
      }
      if (filterType === 'provider') setSearch((prev) => ({ ...prev, providerIds: [nextValue?.value] }));
      if (filterType === 'airport') setSearch((prev) => ({ ...prev, airportIds: [nextValue?.value] }));
      if (filterType === 'tag') setSearch((prev) => ({ ...prev, tags: [nextValue?.value] }));
      resetPage();
    },
    [],
  );

  const tagOptions = useMemo(() => {
    return CONTACT_TAGS.map((tag) => ({ label: tag, value: tag }));
  }, []);

  const pageCounterCopy = useMemo(() => {
    if (!results?.data?.length) return 'No results found';

    return `${results?.page} / ${numberOfPages}`;
  }, [numberOfPages, results?.page, results?.data?.length]);

  const isResultsEmpty = useMemo(() => !results?.data?.length, [results?.data]);
  const isNextDisabled = useMemo(
    () => isResultsEmpty || results?.page >= numberOfPages,
    [isResultsEmpty, numberOfPages, results?.page],
  );
  const isPreviousDisabled = useMemo(() => isResultsEmpty || results?.page <= 1, [isResultsEmpty, results?.page]);

  return (
    <Flex direction="column" {...rest}>
      <Stack direction="column" py={2}>
        <Stack direction="row" alignItems="center">
          <Input
            autoComplete="off"
            variant="filled"
            placeholder="Email, Preferred Name, or Full Name"
            value={search?.query}
            onChange={handleQueryChange}
          />
        </Stack>
        <Stack direction="row" align="center" justify="space-between">
          <Stack direction="row" align="center">
            {search?.providerIds?.map((id) => (
              <Tag key={id}>
                <TagLabel>{id}</TagLabel>
                <TagCloseButton onClick={handleRemove(id, 'provider')} />
              </Tag>
            ))}
            {search?.airportIds?.map((id) => (
              <Tag key={id}>
                <TagLabel>{id}</TagLabel>
                <TagCloseButton onClick={handleRemove(id, 'airport')} />
              </Tag>
            ))}
            {search?.tags?.map((id) => (
              <Tag key={id}>
                <TagLabel>{id}</TagLabel>
                <TagCloseButton onClick={handleRemove(id, 'tag')} />
              </Tag>
            ))}
            {search?.accounts?.map((account) => (
              <Tag key={account?.id}>
                <TagLabel>
                  {account?.name} ({account?.id})
                </TagLabel>
                <TagCloseButton onClick={handleRemove(account?.id, 'account')} />
              </Tag>
            ))}
          </Stack>

          <Button size="sm" variant="ghost" onClick={clearSearch}>
            Clear
          </Button>
        </Stack>
        <Stack direction="row">
          <SimpleGrid columns={2} columnGap={3} rowGap={3} w="full">
            <GridItem colSpan={{ base: 2, md: 1 }}>
              <Label label="Airline" />
              <TypesenseSelect
                onChange={handleFilterChange('provider')}
                value={search?.providerIds?.map((provider) => ({
                  value: provider,
                  label: provider,
                }))}
                shouldProcessValue
                name="associations.providers"
                collection="airlines"
                queryBy="airline_name, airline_id"
                documentLabel="airline_name"
                documentValue="airline_id"
                suffixWithValue
              />
            </GridItem>
            <GridItem colSpan={{ base: 2, md: 1 }}>
              <Label label="Airport" />
              <TypesenseSelect
                onChange={handleFilterChange('airport')}
                value={search?.airportIds?.map((airport) => ({
                  value: airport,
                  label: airport,
                }))}
                shouldProcessValue
                name="associations.airports"
                collection="airports"
                queryBy="airport_name, airport_id"
                documentLabel="airport_name"
                documentValue="airport_id"
                suffixWithValue
              />
            </GridItem>
            <GridItem colSpan={{ base: 2, md: 1 }}>
              <Label label="Tags" />
              <ReactSelect
                onChange={handleFilterChange('tag')}
                name="associations.tags"
                options={tagOptions}
                value={search?.tags?.map((tag) => ({ value: tag, label: tag }))}
                shouldProcessValue
              />
            </GridItem>
            <GridItem colSpan={{ base: 2, md: 1 }}>
              <Label label="Account" />
              <TypesenseSelect
                onChange={handleFilterChange('account')}
                name="associations.accounts"
                shouldProcessValue
                value={search?.accounts?.map((account) => ({
                  value: account?.id,
                  name_label: account?.name,
                  label: `${account?.name} (${account?.id})`,
                }))}
                collection="accounts"
                queryBy="name"
                documentLabel="name"
                documentValue="account_id"
                suffixWithValue
              />
            </GridItem>
          </SimpleGrid>
        </Stack>
      </Stack>
      <Stack direction="row" py={3}>
        <Tag size="lg" borderRadius="full" p={2} pl={3}>
          <Avatar size="xs" name={search?.query?.[0]} mr={2} bgColor="black" />
          <TagLabel>{search?.query}</TagLabel>
          <Button
            variant="ghost"
            borderRadius="full"
            color="blue.300"
            size="sm"
            onClick={() =>
              openModal('Contact', {
                isOpen: true,
                onClose: () => closeModal('Contact'),
                onContactCreate: handleContactCreated,
              })
            }
          >
            <Icon as={HiUserPlus} size={32} color="blue.300" mr={2} />
            Add
          </Button>
        </Tag>
      </Stack>
      <Stack w="full" direction="row" justify="space-between">
        <Button size="sm" variant="outline" isDisabled={isPreviousDisabled} onClick={decrementPage}>
          Previous
        </Button>
        <Tag size="lg" borderRadius="full" py={2} px={4}>
          {pageCounterCopy}
        </Tag>
        <Button size="sm" variant="outline" isDisabled={isNextDisabled} onClick={incrementPage}>
          Next
        </Button>
      </Stack>
      <SimpleGrid columns={3} gap={2} py={3} maxW="full">
        {results?.data?.map(renderContactCard)}
      </SimpleGrid>
    </Flex>
  );
};
